import { useState } from "react";

import { makePostApiCall } from "../../../shared/makeApiCall";
import ModelErrorComponent from "../../../components/modals/ModelErrorComponent";
import { SnackbarData, SnackbarType } from "../../../components/Snackbar";
import { generateUniqueNumber } from "../../../shared/utility";

function isPalletResponse(data) {
  return String(data?.carton_type ?? "").toLowerCase() === "pallet";
}

export function PalletLetDownViewModel(logout, addSnackbar) {
  const [palletPayload, setPalletPayload] = useState(null);
  const [palletScanInput, setPalletScanInput] = useState("");
  const [cartonScanInput, setCartonScanInput] = useState("");
  const [sessionLetDowns, setSessionLetDowns] = useState([]);
  const [palletFetchInProgress, setPalletFetchInProgress] = useState(false);
  const [cartonLetDownInProgress, setCartonLetDownInProgress] = useState(false);
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle ?? ""}
          title={title}
        />
      ),
      open: true,
    });
  };

  const resetPallet = () => {
    setPalletPayload(null);
    setPalletScanInput("");
    setCartonScanInput("");
    setSessionLetDowns([]);
  };

  const fetchPalletByBarcode = async function (rawBarcode) {
    const scannedBarcode = String(rawBarcode ?? "").trim();
    if (!scannedBarcode) {
      return;
    }

    setPalletFetchInProgress(true);
    const callResult = await makePostApiCall(
      `fetchPackagedItemByBarcode/${scannedBarcode}`,
    );
    setPalletFetchInProgress(false);

    if (callResult.success) {
      const data = callResult.data;
      if (!data?.packaged_item) {
        showErrorModal(
          "Invalid response",
          "Could not read pallet details for this barcode.",
        );
        setPalletScanInput("");
        return { success: false };
      }
      if (!isPalletResponse(data)) {
        showErrorModal(
          "Not a pallet",
          "Scan a pallet label. This barcode is not a pallet.",
        );
        setPalletScanInput("");
        return { success: false };
      }
      setPalletPayload(data);
      setPalletScanInput("");
      setCartonScanInput("");
      setSessionLetDowns([]);
      return { success: true, data };
    }

    if (callResult.errorStatus === 401) {
      logout();
      return { success: false };
    }

    const msg =
      callResult.data?.message ??
      callResult.data?.title ??
      "We could not find that pallet.";
    showErrorModal("Error", typeof msg === "string" ? msg : String(msg));
    setPalletScanInput("");
    return { success: false };
  };

  const letDownCarton = async function (rawCartonBarcode) {
    const cartonBarcode = String(rawCartonBarcode ?? "").trim();
    if (!cartonBarcode || !palletPayload) {
      return;
    }

    setCartonLetDownInProgress(true);
    // If the API requires the pallet barcode in the body, extend makePostApiCall here with a payload.
    const callResult = await makePostApiCall(`palletLetDown/${cartonBarcode}`);
    setCartonLetDownInProgress(false);

    if (callResult.success) {
      const data = callResult.data;
      const message =
        data?.message != null ? String(data.message) : "Carton let down.";
      const palletFromResponse = data?.pallet;
      if (
        palletFromResponse &&
        palletPayload?.packaged_item &&
        palletFromResponse.remaining_cartons != null
      ) {
        setPalletPayload((prev) => {
          if (!prev?.packaged_item) return prev;
          return {
            ...prev,
            packaged_item: {
              ...prev.packaged_item,
              carton_count: palletFromResponse.remaining_cartons,
            },
          };
        });
      }
      addSnackbar(
        new SnackbarData(generateUniqueNumber(), message, SnackbarType.Success),
      );
      setSessionLetDowns((prev) => [
        {
          barcode: cartonBarcode,
          message,
          at: new Date().toISOString(),
        },
        ...prev,
      ]);
      setCartonScanInput("");
      return { success: true, data };
    }

    if (callResult.errorStatus === 401) {
      logout();
      return { success: false };
    }

    const msg =
      callResult.data?.message ??
      callResult.data?.title ??
      "Could not let down this carton.";
    showErrorModal("Error", typeof msg === "string" ? msg : String(msg));
    setCartonScanInput("");
    return { success: false };
  };

  return {
    palletPayload,
    palletScanInput,
    setPalletScanInput,
    cartonScanInput,
    setCartonScanInput,
    sessionLetDowns,
    palletFetchInProgress,
    cartonLetDownInProgress,
    emptyModal,
    setEmptyModelOpen,
    showErrorModal,
    fetchPalletByBarcode,
    letDownCarton,
    resetPallet,
  };
}
